import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useRepository } from '@/composables'
import { useStreamStore } from '@/stores/streamStore'
import { getLocalStorage } from '@browser'
import { UAParser } from 'ua-parser-js'
import { mediator } from '@communication'
import { useDocumentVisibility, useEventBus, useThrottleFn, useWindowSize } from '@vueuse/core'
import { useSessionStore } from '@/stores/index'
import { useMeetingStore } from '@/stores'
import { toWsMessage, transform } from '@/messages'

const appInitialState = window.__INITIAL_STATE__?.app

const toStatus = (recommended, available, enabled, on) => {
  if (!recommended) {
    return 'hidden'
  }
  if (!available) {
    return 'not-available'
  }
  return enabled && on ? 'on' : 'muted'
}
export default defineStore('user', () => {
  const id = ref(null)
  const email = ref(null)
  const fullName = ref(null)
  const party = ref(null)
  const qualityNetwork = ref('average')
  const location = ref(null)
  const ua = UAParser()
  const status = 'online'
  const totalWebrtcByte = ref(0)
  const totalWsByte = ref(0)
  const storage = getLocalStorage('devices')
  const dataLimit = ref(storage?.dataLimit || 0)
  const unitData = ref(storage?.unitData || 'MB')
  const visibility = useDocumentVisibility()
  const { width, height } = useWindowSize()
  const streamStore = useStreamStore()
  const { camEnabled, camOn, micEnabled, micOn, camAvailable, micAvailable, hasTorchCapability, torchToggled } = storeToRefs(streamStore)
  const deviceInfo = {
    os: ua.os,
    device: {
      ...ua.device,
      ...(!ua.device.type && { type: 'desktop' }),
    },
    browser: ua.browser,
    cpu: ua.cpu,
    engine: ua.engine,
  }
  /**
   * `not-available` | `hidden` | `on` | `muted`
   */
  const microphoneState = computed(() => toStatus(appInitialState.isMicrophoneRecommended, micAvailable.value, micEnabled.value, micOn.value))
  /**
   * `not-available` | `hidden` | `on` | `muted`
   */
  const cameraState = computed(() => toStatus(appInitialState.isCameraRecommended, camAvailable.value, camEnabled.value, camOn.value))
  const signal = ref(null)
  const profile = computed(() => ({
    microphone: microphoneState.value,
    camera: cameraState.value,
    presence: visibility.value === 'visible',
    viewport: {
      width: width.value,
      height: height.value,
    },
    signal: signal.value,
    qualityNetwork: qualityNetwork.value,
    hasTorchCapability: hasTorchCapability.value,
    torchStatus: torchToggled.value,
  }))
  const networkSpeed = ref({
    uploadSpeed: 0,
    downloadSpeed: 0,
  })
  const hasCameraAvailable = computed(() => cameraState.value !== 'not-available')
  const hasCameraMuted = computed(() => cameraState.value === 'muted')
  const hasMicrophoneAvailable = computed(() => microphoneState.value !== 'not-available')
  const hasMicrophoneMuted = computed(() => microphoneState.value === 'muted')

  const isClient = computed(() => party.value === 'client')
  const isAssistant = computed(() => party.value === 'assistant')
  const isObserver = computed(() => party.value === 'observer')
  const consumptionData = computed(() => ((totalWebrtcByte.value + totalWsByte.value) / 1024 ** (unitData.value === 'KB' ? 1 : unitData.value === 'MB' ? 2 : 3)).toFixed(2))
  const session = useSessionStore()
  const members = computed(() =>
    Object.values(
      [...session.info.participants, ...session.users].reduce((acc, obj) => {
        acc[obj.id] = obj
        return acc
      }, {})
    )
  )

  const isInLobby = computed(() => {
    return useMeetingStore().isLobby
  })

  const wifiMap = new Map([
    ['good', { name: 'good-wifi', color: 'green' }],
    ['average', { name: 'average-wifi', color: '#979729' }],
    ['poor', { name: 'poor-wifi', color: 'red' }],
    ['offline', { name: 'wifi-off', color: 'black' }],
  ])

  const networkIcon = quality => wifiMap.get(quality) || { name: 'average-wifi', color: 'grey' }

  function setUser(id, email, fullName, party) {
    this.$patch({
      id,
      email,
      fullName,
      party,
    })
  }

  function setLocation(location) {
    this.$patch({
      location,
    })
  }

  function setSignal(value) {
    signal.value = value
  }

  function disjoin() {
    mediator.session
      .disjoin()
      .then(() => {
        // TODO
      })
      .catch(error => {
        // TODO
        console.warn('disjoin error', error)
      })
  }

  function changeRole(role) {
    // TODO verify here
    party.value = role
    roles.value = appInitialState?.roles?.[role] || []
  }

  function setQualityNetwork(quality) {
    qualityNetwork.value = quality
  }

  function setNetworkSpeed(networkSpeed) {
    this.$patch({
      networkSpeed,
    })
  }

  function _getSize(data) {
    if (data instanceof Blob) {
      return data.size
    } else if (data instanceof ArrayBuffer) {
      return data.byteLength
    } else {
      return new TextEncoder().encode(data).length
    }
  }
  setInterval(async () => {
    const totalBits = (await mediator.connection?.getTotalBits()) || 0;
    totalWebrtcByte.value = Math.round(totalBits / 8);
  }, Number(import.meta.env.VITE_DURATION_OF_NETWORK_QUALITY_MONITORING))

  useEventBus('set-total-webrtc-byte').on((size) => {
    totalWebrtcByte.value = size;
  });

  useEventBus('increase-ws-byte').on((size) => {
    const add = typeof size === 'number' ? size : _getSize(size);
    totalWsByte.value += add;
  });

  function setDataLimit(dataLimit) {
    this.$patch({
      dataLimit,
    })
  }

  function setUnitData(unitData) {
    this.$patch({
      unitData,
    })
  }


  const broadcastStatic = () => mediator.broadcast({ type: 'profile:static', data: { id: id.value, deviceInfo: deviceInfo } })
  const broadcastDynamic = (profile) =>
    mediator.broadcast({ type: 'profile:sync', data: { id: id.value, profile } })
  const broadcastProfile = () => {
    broadcastStatic()
    broadcastDynamic(profile.value)
  }

  let ready = false
  useEventBus('strategy:ready').on(() => {
    ready = true
    mediator.broadcast({ type: 'profile:create', data: { id: id.value } })
    broadcastProfile()
  })
  useEventBus('profile:create').on(() => broadcastProfile())
  watch(profile, (profile) => ready && broadcastDynamic(profile), { deep: true })
  const { sendWsEvent } = useRepository()
  const throttleTransformMessage = useThrottleFn((data) => {
    sendWsEvent(toWsMessage(transform.viewportMessage(data)))
  }, 1000)
  watch(
    [isClient, width, height],
    ([client, w, h]) => {
      if (client) {
        throttleTransformMessage({ width: w, height: h })
      }
    },
    { immediate: true },
  )
  watch(
    [isClient, visibility],
    ([client, state]) => {
      if (client) {
        if (state === 'hidden') {
          mediator.broadcast({ type: 'video:suspended' })
        } else if (state === 'visible') {
          mediator.broadcast({ type: 'video:resumed' })
        }
      }
    },
    { immediate: true },
  )
  useEventBus('torch:toggle').on(async () => {
    if (isClient.value) {
      streamStore.toggleTorch()
    }
  })
  useEventBus('participant:mute').on(data => {
    const { id: memberId, deviceType } = data
    if (Number(id.value) === Number(memberId)) {
      if (deviceType === 'audio') {
        micEnabled.value = false
      } else if (deviceType === 'video') {
        camEnabled.value = false
      } else if (deviceType === 'torch' && torchToggled.value) {
        streamStore.toggleTorch()
      }
    }
  })
  return {
    networkIcon,
    isInLobby,
    members,
    email,
    fullName,
    id,
    party,
    location,
    ua,
    profile,
    status,
    networkSpeed,
    qualityNetwork,
    dataLimit,
    unitData,
    consumptionData,
    hasMicrophoneAvailable,
    hasMicrophoneMuted,
    hasCameraAvailable,
    hasCameraMuted,
    isClient,
    isAssistant,
    isObserver,
    setUser,
    setLocation,
    setSignal,
    disjoin,
    changeRole,
    deviceInfo,
    setQualityNetwork,
    setNetworkSpeed,
    setDataLimit,
    setUnitData,
  }
})
